import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { startTransition, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import AdminTradeRulesControls from 'src/components/Admin/AdminTradeRuleControls';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import {
  DEFAULT_LIST_PAGE_SIZE, LIST_FILTER_ALL, LOAD_MORE, PLATFORM_MODE_REBATE, REFETCH,
} from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { valueRangePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import logger from 'src/util/logger';
import { dateToTimeInTimezone, getTimezone } from 'src/util/time';
import { getPriceRange } from 'src/util/tradeRule';

import AdminTradeRuleListTrader from './AdminTradeRuleListTrader';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more trade rules. Please try again.',
  },
});

const AdminTradeRuleListFragment = graphql`
  fragment AdminTradeRuleList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminTradeRuleListRefetchQuery")
  @argumentDefinitions(
    count: { type: "Int" }
    cursor: { type: "String" }
    state: { type: "TradeRuleState" }
    type: { type: "TradeType" }
    start: { type: "Timestamp" }
    finish: { type: "Timestamp" }
  )
  {
    id
    rules(
      first: $count
      after: $cursor
      state: $state
      type: $type
      start: $start
      finish: $finish
    ) @connection(key: "AdminTradeRuleList_rules") {
      edges {
        cursor
        node {
          uuid
          tradeType
          clauses {
            edges {
              node {
                price
              }
            }
          }
          start
          finish
          state
          buyer {
            tradePoint { uuid type }
            userId
            community {
              uuid
              title
              active { start finish }
            }
            user {
              uuid
              email
              givenName
              familyName
              active { start finish }
            }
          }
          seller {
            tradePoint { uuid type }
            userId
            community {
              uuid
              title
              active { start finish }
            }
            user {
              uuid
              email
              givenName
              familyName
              active { start finish }
            }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={7} rowCount={3} />;
}

/**
 * Builds the URL query string that mirrors the current trade rule filters.
 * @param {object} filter - Current filter.
 * @param {string|null} filter.state - Trade rule state filter.
 * @param {string|null} filter.type - Trade type filter.
 * @param {DateTime|null} date - Selected date.
 * @param {string} timezone - Timezone the date is expressed in.
 * @returns {string} The `search` part of the URL, with a leading `?`, or an empty string.
 */
function buildFilterSearch({ state, type }, date, timezone) {
  const queryParameters = new URLSearchParams();
  if (state) {
    queryParameters.append('state', state);
  }
  if (type) {
    queryParameters.append('type', type);
  }
  if (date) {
    queryParameters.append('date', date.toISODate({ zone: timezone }));
  }
  const search = queryParameters.toString();
  return search ? `?${search}` : '';
}

/**
 * Admin list of trade rules, filterable by date, state and type, with load-more
 * pagination. Field errors and failed filter refetches throw to the route's
 * PlaceholderErrorBoundary; load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @param {object} props.match - Found route match, used for the current pathname.
 * @param {object} props.router - Found router, used to sync filters into the URL.
 * @param {object} props.variables - Route query variables holding the initial filters.
 * @param {Function} props.filtersAppliedFunc - Called with the refetch variables after
 *   a filter or date refetch succeeds, so the route can retry with them.
 * @returns {React.ReactElement} AdminTradeRuleList component.
 */
function AdminTradeRuleList({
  viewer: viewerRef = null, filtersAppliedFunc, match, router, variables,
}) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();
  const {
    data: viewer, loadNext, hasNext, isLoadingNext, refetch,
  } = usePaginationFragment(AdminTradeRuleListFragment, viewerRef);

  const [date, setDate] = useState(() => (
    variables?.start ? DateTime.fromSeconds(variables.start) : null
  ));
  const [filter, setFilter] = useState({
    state: variables?.state || null,
    type: variables?.type || null,
  });
  const [updating, setUpdating] = useState(null);
  const [paginationError, setPaginationError] = useState(null);

  /**
   * Refetches trade rules for the given filters and, on success, mirrors them
   * into the URL.
   * @param {object} nextFilter - Filter to apply.
   * @param {DateTime|null} nextDate - Date to apply.
   */
  const doRefetch = (nextFilter, nextDate) => {
    const timezone = getTimezone();
    const refetchVariables = {
      count: DEFAULT_LIST_PAGE_SIZE,
      state: nextFilter.state,
      type: nextFilter.type,
      start: null,
      finish: null,
    };
    if (nextDate) {
      const dateInTimezone = dateToTimeInTimezone(nextDate, timezone);
      refetchVariables.start = dateInTimezone.startOf('day').toSeconds();
      refetchVariables.finish = dateInTimezone.startOf('day').plus({ days: 1 }).toSeconds();
    }

    setUpdating(REFETCH);
    // A transition keeps the refetch from suspending the whole route to its
    // Suspense fallback; the heading, filters and table frame stay mounted.
    startTransition(() => {
      refetch(refetchVariables, {
        fetchPolicy: 'network-only',
        // A failed refetch throws to the route's PlaceholderErrorBoundary, whose
        // retry reloads the last applied filters, which are also the ones in the URL.
        onComplete: (error) => {
          if (!error) {
            filtersAppliedFunc(refetchVariables);
            // The refetch already used the property timezone. Rematching would
            // rerun the route's prepareVariables, which falls back to DEFAULT_TIMEZONE
            // without a tz param, and replace these results with a different day.
            router.replace({
              pathname: match?.location?.pathname,
              search: buildFilterSearch(nextFilter, nextDate, timezone),
              hash: '',
              doNotRerunMatch: true,
            });
          }
          setUpdating(null);
        },
      });
    });
  };

  /**
   * Updates the selected date and refetches.
   * @param {DateTime} nextDate - The selected date.
   */
  const dateUpdateFunc = (nextDate) => {
    if (updating) {
      return;
    }

    setDate(nextDate);
    doRefetch(filter, nextDate);
  };

  /**
   * Updates the state or type filter and refetches. Selecting the active value
   * clears it; `LIST_FILTER_ALL` clears every filter and the date.
   * @param {string} key - The filter key to update (`state` or `type`).
   * @param {string} value - The new filter value.
   */
  const filterFunc = (key, value) => {
    if (updating) {
      return;
    }

    const nextFilter = { ...filter };
    let nextDate = date;

    if (value === LIST_FILTER_ALL) {
      nextFilter.state = null;
      nextFilter.type = null;
      nextDate = null;
    } else if (filter[key] === value) {
      nextFilter[key] = null;
    } else {
      nextFilter[key] = value;
    }

    setFilter(nextFilter);
    setDate(nextDate);
    doRefetch(nextFilter, nextDate);
  };

  /**
   * Loads the next page of trade rules. A failure keeps the loaded rules and
   * shows the inline pagination error modal.
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext || updating) {
      return;
    }

    setPaginationError(null);
    setUpdating(LOAD_MORE);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
        setUpdating(null);
      },
    });
  };

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminTradeRuleList: the route query returned no viewer');
  }

  const { rules } = viewer;

  // i18n messages
  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.page_title',
    defaultMessage: 'Enosi Admin - Trade Rules',
  });
  const adminBreadcrumb = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.breadcrumb.admin',
    defaultMessage: 'Admin',
  });
  const tradeRulesBreadcrumb = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.breadcrumb.trade_rules',
    defaultMessage: 'Trade Rules',
  });
  const tradeRulesHeading = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.heading',
    defaultMessage: 'Trade Rules',
  });

  // Table headers
  const statusHeader = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.header.status',
    defaultMessage: 'Status',
  });
  const idHeader = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.header.id',
    defaultMessage: 'ID',
  });
  const stateHeader = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.header.state',
    defaultMessage: 'State',
  });
  const typeHeader = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.header.type',
    defaultMessage: 'Type',
  });
  const buyerHeader = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.header.buyer',
    defaultMessage: 'Buyer',
  });
  const sellerHeader = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.table.header.seller',
    defaultMessage: 'Seller',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_trade_rule.admin_trade_rule_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more trade rules to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: adminBreadcrumb, path: '/admin' },
          { name: tradeRulesBreadcrumb },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start mb-4">{tradeRulesHeading}</h1>
      </div>

      <AdminTradeRulesControls
        date={date}
        dateUpdateFunc={dateUpdateFunc}
        filter={filter}
        filterFunc={filterFunc}
      />

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{statusHeader}</th>
                <th scope="col">{idHeader}</th>
                <th scope="col">{stateHeader}</th>
                <th scope="col">{typeHeader}</th>
                <th scope="col">
                  {APIConfig().MODE === PLATFORM_MODE_REBATE ? 'Discount' : 'Price'}
                </th>
                <th scope="col">{buyerHeader}</th>
                <th scope="col">{sellerHeader}</th>
              </tr>
            </thead>
            <tbody>
              {
                rules && rules.edges && rules.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, tradeType, start,
                    finish, state, buyer, seller,
                  } = node;
                  const active = { start, finish };
                  const priceRange = getPriceRange(node);

                  return (
                    <tr key={`admin-trade-rule-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <UUID uuid={uuid} url={`/admin/trade-rules/${uuid}`} />
                        <br />
                        <BadgeTradeRuleState state={state} />
                        <br />
                        <BadgeTradeType type={tradeType} />
                      </th>
                      <td>
                        {valueRangePerUnitWithStrong(intl, intlCurrency, priceRange)}
                      </td>
                      <td>
                        <AdminTradeRuleListTrader trader={buyer} />
                      </td>
                      <td>
                        <AdminTradeRuleListTrader trader={seller} />
                      </td>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={updating === LOAD_MORE}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminTradeRuleList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  filtersAppliedFunc: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  variables: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AdminTradeRuleList;
